<template>
  <div
    class="rounded-lg border border-gray-200 bg-gray-50 text-xs"
    data-cy="json-viewer"
  >
    <div
      class="flex items-center justify-between gap-2 border-b border-gray-200 px-3 py-2"
    >
      <span class="font-semibold text-gray-700">{{ title }}</span>
      <button
        v-if="!isEmpty"
        type="button"
        class="rounded px-2 py-1 font-medium text-blue-700 hover:bg-blue-50"
        data-cy="json-viewer-copy"
        @click="copy"
      >
        {{ copied ? "Copied" : "Copy" }}
      </button>
    </div>

    <p
      v-if="isEmpty"
      class="m-0 px-3 py-2 text-gray-500"
      data-cy="json-viewer-empty"
    >
      {{ emptyText }}
    </p>

    <pre
      v-else-if="!entries.length"
      class="m-0 overflow-x-auto whitespace-pre-wrap break-all px-3 py-2 font-mono text-gray-900"
      data-cy="json-viewer-raw"
      >{{ fullText }}</pre>

    <div
      v-else
      class="overflow-x-auto px-3 py-2 font-mono text-gray-900"
      data-cy="json-viewer-tree"
    >
      <div>{{ brackets[0] }}</div>
      <div
        v-for="(entry, index) in entries"
        :key="entry.key"
        class="pl-4"
        :data-cy="`json-viewer-entry:${entry.key}`"
      >
        <button
          v-if="entry.collapsible"
          type="button"
          class="-ml-4 w-4 text-gray-500"
          :aria-expanded="!collapsed.has(entry.key)"
          :aria-label="`Toggle ${entry.key}`"
          data-cy="json-viewer-toggle"
          @click="toggle(entry.key)"
        >
          {{ collapsed.has(entry.key) ? "▸" : "▾" }}
        </button>
        <span v-if="!isArray" class="text-purple-700">"{{ entry.key }}"</span
        ><span v-if="!isArray">: </span>
        <span
          v-if="collapsed.has(entry.key)"
          class="text-gray-500"
          data-cy="json-viewer-summary"
          >{{ entry.summary }}</span
        >
        <pre
          v-else
          class="m-0 inline whitespace-pre-wrap break-all font-mono"
          >{{ entry.text }}</pre>
        <span v-if="index < entries.length - 1">,</span>
      </div>
      <div>{{ brackets[1] }}</div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch } from "vue";

const props = withDefaults(
  defineProps<{
    value: unknown;
    title?: string;
    emptyText?: string;
  }>(),
  {
    title: "Payload",
    emptyText: "Nothing was recorded."
  }
);

interface Entry {
  key: string;
  text: string;
  summary: string;
  collapsible: boolean;
}

// A string that holds JSON is shown as the JSON it holds; any other string
// is shown as-is.
const parsed = computed<unknown>(() => {
  if (typeof props.value !== "string") return props.value;
  try {
    return JSON.parse(props.value);
  } catch {
    return props.value;
  }
});

const isEmpty = computed(
  () =>
    parsed.value === null || parsed.value === undefined || parsed.value === ""
);

const isArray = computed(() => Array.isArray(parsed.value));

const fullText = computed(() =>
  typeof parsed.value === "string"
    ? parsed.value
    : JSON.stringify(parsed.value, null, 2)
);

const brackets = computed(() => (isArray.value ? ["[", "]"] : ["{", "}"]));

function summarize(value: unknown): string {
  if (Array.isArray(value)) {
    return `[…] ${value.length} ${value.length === 1 ? "item" : "items"}`;
  }
  const count = Object.keys(value as object).length;
  return `{…} ${count} ${count === 1 ? "key" : "keys"}`;
}

const entries = computed<Entry[]>(() => {
  const value = parsed.value;
  if (value === null || typeof value !== "object") return [];

  return Object.entries(value as Record<string, unknown>).map(([key, v]) => {
    const collapsible = v !== null && typeof v === "object";
    return {
      key,
      text: JSON.stringify(v, null, 2) ?? "null",
      summary: collapsible ? summarize(v) : "",
      collapsible
    };
  });
});

const collapsed = ref(new Set<string>());
const copied = ref(false);

watch(
  () => props.value,
  () => {
    collapsed.value = new Set();
    copied.value = false;
  }
);

function toggle(key: string) {
  const next = new Set(collapsed.value);
  if (next.has(key)) next.delete(key);
  else next.add(key);
  collapsed.value = next;
}

async function copy() {
  try {
    await navigator.clipboard.writeText(fullText.value);
    copied.value = true;
  } catch {
    window.notify.error("Could not copy to the clipboard.");
  }
}
</script>
